import { Outlet } from 'react-router-dom';
import type { ReactNode } from 'react';

import { PermissionDeniedPage } from '@core/components/Error/PermissionDeniedPage';
import { LoadingFallback } from '@core/components/Loading/LoadingFallback';
import { useAppSelector } from '@core/store/hooks';
import type { UserRole } from '@/types/user';

interface RequireRoleProps {
  role: UserRole | readonly UserRole[];
  children?: ReactNode;
}

/**
 * ONE guard for all five roles — the four copies it replaces drifted apart, and each
 * redirected to `/dashboard`, hiding the reason.
 *
 * Two rules it exists to enforce:
 * - Spinner while resolving, deny only when the role is KNOWN, so a permitted user never
 *   sees a denied flash on the first paint after a reload.
 * - Render `PermissionDeniedPage` IN PLACE, never `<Navigate>` — redirecting loses the URL
 *   the user asked for.
 */
export function RequireRole({ role, children }: RequireRoleProps) {
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const userRole = useAppSelector((s) => s.auth.user?.role);

  if (isAuthenticated && !userRole) return <LoadingFallback />;

  const allowed = Array.isArray(role) ? role : [role];
  if (!userRole || !allowed.includes(userRole)) return <PermissionDeniedPage />;

  return <>{children ?? <Outlet />}</>;
}
